import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLocale } from '@/i18n/LocaleContext';

export const Hero = () => {
  const { t } = useLocale();
  const reduced = useReducedMotion();

  const fadeUp = (delay = 0) => ({
    initial: reduced ? {} : { opacity: 0, y: 24 },
    animate: reduced ? {} : { opacity: 1, y: 0 },
    transition: { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] },
  });

  return (
    <section id="top" data-testid="hero-section" className="relative overflow-hidden pt-28 sm:pt-36 pb-16 sm:pb-24">
      {/* Lantern chain texture, very subtle */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{ backgroundImage: 'url(/assets/04-backgrounds/lantern-chain-pattern.svg)', backgroundSize: '480px' }}
      />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-10 items-center">
          <div>
            <motion.h1
              {...fadeUp(0)}
              data-testid="hero-title"
              className="font-display mt-2 text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight text-foreground"
            >
              {t.hero.title}
            </motion.h1>

            <motion.p {...fadeUp(0.08)} className="mt-6 text-base sm:text-lg leading-relaxed text-muted-foreground max-w-xl">
              {t.hero.body}
            </motion.p>

            <motion.div {...fadeUp(0.16)} className="mt-8 flex flex-wrap items-center gap-4">
              <Button
                asChild
                size="lg"
                data-testid="hero-cta-story"
                className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold text-base h-12 px-7 border-2 border-primary shadow-[4px_4px_0_hsl(var(--ink))] hover:shadow-[2px_2px_0_hsl(var(--ink))]"
              >
                <a href="#story">
                  {t.hero.ctaStory}
                  <ArrowDown className="ml-2 h-4 w-4" aria-hidden="true" />
                </a>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                data-testid="hero-cta-tokenomics"
                className="h-12 px-7 text-base font-semibold border-2 border-foreground/30 bg-transparent text-foreground hover:bg-secondary hover:text-primary hover:border-primary"
              >
                <a href="#tokenomics">{t.hero.ctaTokenomics}</a>
              </Button>
            </motion.div>

            <motion.ul {...fadeUp(0.24)} data-testid="hero-chips" className="mt-10 flex flex-wrap gap-3">
              {t.hero.chips.map((chip) => (
                <li
                  key={chip}
                  className="rounded-full border border-border bg-card px-4 py-2 text-xs sm:text-sm font-medium text-muted-foreground"
                >
                  {chip}
                </li>
              ))}
            </motion.ul>
          </div>

          {/* Portrait: single visual source of truth */}
          <motion.div
            initial={reduced ? {} : { opacity: 0, scale: 0.96 }}
            animate={reduced ? {} : { opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-auto w-full max-w-md lg:max-w-lg"
          >
            <img
              src="/assets/04-props/red-lantern.png"
              alt=""
              aria-hidden="true"
              className="lantern-sway absolute -top-16 right-4 sm:right-8 w-16 sm:w-20 z-10 drop-shadow-lg"
            />
            <div className="hero-frame rounded-2xl overflow-hidden rotate-[-1.5deg] bg-card">
              <img
                src="/assets/00-reference/peipei-style-master.jpg"
                alt={t.hero.altPortrait}
                data-testid="hero-portrait"
                className="w-full h-auto block"
                loading="eager"
              />
            </div>
            <img
              src="/assets/01-mascot/cutouts/peipei-tea-flat.png"
              alt=""
              aria-hidden="true"
              className="gentle-float absolute -bottom-10 -left-6 sm:-left-12 w-28 sm:w-36 rotate-[6deg] drop-shadow-xl"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
